import { Controller } from '@hotwired/stimulus';

/**
 * Planning Impact Revalidation Controller
 *
 * Drives the Planning "Décalage en cascade" TREE slide-over. The body is a FLAT
 * list of dependent phases, one `<li role="treeitem">` per node, in pre-order
 * (a parent line always precedes its dependent lines). The tree is carried by
 * `data-parent-id` (and `aria-level` for assistive tech), NEVER by DOM nesting:
 * the browser HTML parser caps a tree at 512 elements, and a nested
 * `<li>` + `<ul role="group">` per level re-attached the nodes past ~250 levels
 * as siblings (measured in Chromium 145, revue du 03/10). The relations are read
 * once, at connect, into an id → parent-id index.
 *
 * Each shiftable line carries a per-node shift toggle (a real
 * `<input type="checkbox" role="switch">` from Ui::SwitchComponent).
 *
 * The critical contract: only EFFECTIVELY-shifted nodes must POST. A node is
 * "effective" when its own switch is ON **and** every ancestor is effective.
 * A non-effective node's switch is `disabled` — and a disabled checkbox is not
 * serialized by the form, so the server (CascadeShiftService) only ever receives
 * the effectively-shifted ids.
 *
 * Manual chevron collapse is a SEPARATE concern from shift: a collapsed-but-
 * effective subtree stays enabled and still POSTs (its inputs are never
 * disabled), it is only visually hidden. A line is shown iff its parent line is
 * shown, effective and not collapsed.
 *
 * IMPOSED roots (calendar change): a sub-lot the server writes whatever the
 * user picks (prolonged or pushed by a non-worked day) is rendered WITHOUT a
 * switch and carries `data-impact-imposed` on its `<li>`. It is ALWAYS
 * effective: its dependent lines stay shown and follow their own switch (judged
 * by its missing checkbox, it would hide and never post them — revue RF-T3).
 * « Tout décaler » / « Tout figer » leave it alone, it is never greyed, and the
 * manual chevron collapse still works on it.
 *
 * Targets:
 * - row: every `<li role="treeitem">`, undated lines included (they hide with
 *   their parent). Carries `data-node-id` and, below a root, `data-parent-id`.
 * - node: the rows that carry a decision state: shiftable lines (with a switch)
 *   and imposed roots (`data-impact-imposed`, no switch). An undated line is a
 *   `row` only.
 *
 * Attributes read on a row: `data-node-id`, `data-parent-id`,
 * `data-impact-imposed` (presence only).
 *
 * Actions:
 * - refresh: re-run the apply pass after a switch toggles (wired per-switch).
 * - toggleCollapse: flip manual collapse for the node owning the clicked chevron.
 * - selectAll: turn every switch ON and clear all manual collapse, then refresh.
 * - selectNone: turn every switch OFF, then refresh (imposed roots stay effective).
 *
 * @example
 * <div data-controller="planning-impact-revalidation">
 *   <button data-action="planning-impact-revalidation#selectAll">Tout</button>
 *   <button data-action="planning-impact-revalidation#selectNone">Aucune</button>
 *   <ul role="tree">
 *     <li role="treeitem" aria-level="1" aria-setsize="1" aria-posinset="1" aria-expanded="true"
 *         data-planning-impact-revalidation-target="row node" data-node-id="42">
 *       <div data-impact-role="card">
 *         <button data-action="click->planning-impact-revalidation#toggleCollapse">⌄</button>
 *         <span data-impact-role="new-date" data-new-date="12/01/2026">12/01/2026</span>
 *         <span data-impact-role="badge">+3 j</span>
 *         <input type="checkbox" role="switch" name="shift[42]" checked
 *                data-action="change->planning-impact-revalidation#refresh">
 *       </div>
 *     </li>
 *     <li role="treeitem" aria-level="2" aria-setsize="1" aria-posinset="1"
 *         data-planning-impact-revalidation-target="row node" data-node-id="43" data-parent-id="42">…</li>
 *     <!-- Imposed root: no switch, always effective; 51 follows its own switch. -->
 *     <li role="treeitem" aria-level="1" aria-setsize="1" aria-posinset="1" aria-expanded="true"
 *         data-planning-impact-revalidation-target="row node" data-node-id="50" data-impact-imposed>
 *       <div data-impact-role="card">
 *         <button data-action="click->planning-impact-revalidation#toggleCollapse">⌄</button>
 *         <span data-impact-role="new-date" data-new-date="19/01/2026">19/01/2026</span>
 *         <span data-impact-role="badge">+2 j</span>
 *       </div>
 *     </li>
 *     <li role="treeitem" aria-level="2" aria-setsize="1" aria-posinset="1"
 *         data-planning-impact-revalidation-target="row node" data-node-id="51" data-parent-id="50">…</li>
 *   </ul>
 * </div>
 */
export default class extends Controller {
  static targets = ['row', 'node'];

  connect() {
    // Set of node ids the user has manually collapsed via the chevron.
    // Separate from shift state: collapse hides, it never disables/POST-excludes.
    this.collapsed = new Set();
    this.#buildIndex();
    this.#apply();
  }

  // A switch toggled — reconcile the whole tree.
  refresh() {
    this.#apply();
  }

  // Flip the manual collapse state of the node owning the clicked chevron.
  toggleCollapse(event) {
    const node = event.target.closest('[data-planning-impact-revalidation-target~="node"]');
    if (!node) return;

    const id = node.dataset.nodeId;
    if (this.collapsed.has(id)) {
      this.collapsed.delete(id);
    } else {
      this.collapsed.add(id);
    }
    this.#apply();
  }

  // "Tout" — turn every switch ON and reveal everything. An imposed root has no
  // decision to flip.
  selectAll() {
    this.nodeTargets.forEach((node) => {
      if (this.#isImposed(node)) return;
      const input = this.#switchFor(node);
      if (input) input.checked = true;
    });
    this.collapsed.clear();
    this.#apply();
  }

  // "Aucune" — turn every switch OFF. An imposed root stays effective: only its
  // dependent lines freeze.
  selectNone() {
    this.nodeTargets.forEach((node) => {
      if (this.#isImposed(node)) return;
      const input = this.#switchFor(node);
      if (input) input.checked = false;
    });
    this.#apply();
  }

  // ── Index ───────────────────────────────────────────────────────────────
  // Built ONCE: the modal body is never re-rendered in place (a new cascade
  // replaces the whole body, which reconnects the controller).
  #buildIndex() {
    this.rows = this.rowTargets;
    this.nodes = new Set(this.nodeTargets);
    // id → parent id (null at a root), and the ids that have dependent lines.
    this.parentOf = new Map();
    this.parentIds = new Set();
    this.rows.forEach((row) => {
      const parentId = row.dataset.parentId || null;
      this.parentOf.set(row.dataset.nodeId, parentId);
      if (parentId) this.parentIds.add(parentId);
    });
  }

  // ── Apply pass ──────────────────────────────────────────────────────────
  // ONE pass in document order, which the server emits in pre-order: a parent's
  // effective and shown states are known before its dependent lines. O(n), the
  // depth of the tree does not matter.
  #apply() {
    const effective = new Map();
    const shown = new Map();

    this.rows.forEach((row) => {
      const id = row.dataset.nodeId;
      const parentId = this.parentOf.get(id);
      const isRoot = parentId === null;
      const parentEffective = isRoot || effective.get(parentId) === true;

      // Visibility: hidden under a parent that is hidden, collapsed or not
      // effective (the two independent reasons, inherited down the tree).
      const isShown =
        isRoot ||
        (shown.get(parentId) === true && parentEffective && !this.collapsed.has(parentId));
      shown.set(id, isShown);
      row.hidden = !isShown;

      // An undated line has nothing to decide: no switch, no appearance.
      if (!this.nodes.has(row)) return;

      const input = this.#switchFor(row);
      // An imposed root is written whatever the user picks: always effective,
      // so its dependent lines stay shown and post per their own switch.
      const isEffective = this.#isImposed(row) || (Boolean(input?.checked) && parentEffective);
      effective.set(id, isEffective);

      // 1. Disabled: a child whose parent is not effective cannot be shifted.
      //    Roots are never disabled. A disabled input is not POSTed.
      if (input) input.disabled = !isRoot && !parentEffective;

      // 2. Card appearance.
      this.#applyAppearance(row, isEffective);

      // 3. aria-expanded + chevron (lines with dependent lines only).
      this.#applyExpanded(row, isEffective);
    });
  }

  // Grey out the card when not effective; restore otherwise. Hide the +N j badge
  // and blank the new-date when not effective, restoring from `data-new-date`.
  #applyAppearance(node, isEffective) {
    const card = node.querySelector('[data-impact-role="card"]');
    if (card) card.classList.toggle('opacity-50', !isEffective);

    const newDate = node.querySelector(
      ':scope > [data-impact-role="card"] [data-impact-role="new-date"]'
    );
    if (newDate) {
      newDate.textContent = isEffective ? newDate.dataset.newDate : '—';
    }

    const badge = node.querySelector(
      ':scope > [data-impact-role="card"] [data-impact-role="badge"]'
    );
    if (badge) badge.classList.toggle('hidden', !isEffective);
  }

  // aria-expanded must reflect the ACTUAL visibility of the dependent lines
  // (manually collapsed OR not effective hides them), so a screen reader never
  // announces an expanded node whose children are hidden.
  #applyExpanded(node, isEffective) {
    const id = node.dataset.nodeId;
    if (!this.parentIds.has(id)) return; // leaf — no chevron, no aria-expanded

    const manuallyCollapsed = this.collapsed.has(id);
    node.setAttribute('aria-expanded', String(!manuallyCollapsed && isEffective));

    // Rotate the chevron icon to mirror the manual collapse state.
    const chevron = node.querySelector(
      ':scope > [data-impact-role="card"] [data-action*="toggleCollapse"] svg'
    );
    if (chevron) chevron.classList.toggle('-rotate-90', manuallyCollapsed);
  }

  // `data-impact-imposed` (presence only): a root the server writes regardless.
  #isImposed(row) {
    return row.dataset.impactImposed !== undefined;
  }

  // The shift toggle for a node (sr-only but a real input).
  #switchFor(node) {
    return node.querySelector(':scope > [data-impact-role="card"] input[type="checkbox"]');
  }
}
